import type {
  Color,
  Degree,
  Distance,
  Page,
  Pixel,
  PlotMedia,
  PlotPath,
  PlotRect,
  PlotText,
  PlottingContext,
  Point,
  Rectangle,
  Scale,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import {
  ComputeScale,
  CreateColorConvert,
  CreateContext,
  HorizontalAlign,
  HslToRgb,
  PlotFunction,
  Rotate,
  ToRadians,
  VerticalAlign,
} from "@pdfking/renderer-api"
import type { PdfiumApi, PdfiumTypeMap } from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { IterableToArray, ToBlob } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { ReadDocument } from "../functions/ReadDocument"
import { SaveDocument } from "../functions/SaveDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

const { abs, cos, sin, round } = Math

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]
type PointerPageObject = PdfiumTypeMap["FPDF_PAGEOBJECT"]
type Pointer = PdfiumTypeMap["void*"]

type PageBlob = {
  readonly doc: PointerDoc
  readonly page: PointerPage
  readonly scale: Scale
  readonly trimBox: SpatialRectangle<Pixel>
  readonly close: () => Promise<void>
}

type CurrentPlottingContext = {
  readonly api: PdfiumApi
  readonly doc: PointerDoc
  readonly page: PointerPage
  readonly scale_bleed: Pixel
  readonly stroke_width: Pixel
  readonly get_xy: (point: Point<Distance>) => Point<Pixel>
  readonly get_measures: (
    position: SpatialRectangle<Distance>
  ) => SpatialRectangle<Pixel>
  readonly get_color: (id: string) => Color | undefined
  readonly get_page: (blob_id: string) => Promise<PageBlob | undefined>
}

// Encode string as UTF-16LE for pdfium text
const encodeUtf16Le = (
  { writeBytes }: PdfiumApi,
  str: string
): { pointer: Pointer; free: () => void } => {
  const utf16 = new Uint16Array(str.length + 1)
  for (let i = 0; i < str.length; i++) {
    utf16[i] = str.charCodeAt(i)
  }
  utf16[str.length] = 0
  const bytes = new Uint8Array(utf16.buffer)
  return writeBytes(bytes)
}

const to255 = (v: number) => round(v * 255)

const CreateColorSetter = (
  rgbFn: "FPDFPageObj_SetFillColor" | "FPDFPageObj_SetStrokeColor",
  cmykFn:
    | "PDFKING_PageObj_SetFillColorCMYK"
    | "PDFKING_PageObj_SetStrokeColorCMYK"
) =>
  CreateColorConvert<(api: PdfiumApi, obj: PointerPageObject) => Promise<void>>(
    ({ r, g, b, a }) =>
      async ({ call }, obj) => {
        await call(rgbFn, obj, to255(r), to255(g), to255(b), to255(a))
      },
    ({ c, m, y, k, a }) =>
      async ({ call }, obj) => {
        await call(
          cmykFn,
          obj,
          to255(c),
          to255(m),
          to255(y),
          to255(k),
          to255(a)
        )
      },
    ({ h, s, l, a }) =>
      async ({ call }, obj) => {
        const { r, g, b } = HslToRgb({ h, s, l, a })
        await call(rgbFn, obj, to255(r), to255(g), to255(b), to255(a))
      }
  )

const setFillColor = CreateColorSetter(
  "FPDFPageObj_SetFillColor",
  "PDFKING_PageObj_SetFillColorCMYK"
)

const setStrokeColor = CreateColorSetter(
  "FPDFPageObj_SetStrokeColor",
  "PDFKING_PageObj_SetStrokeColorCMYK"
)

// Read box from page (returns x, y, width, height)
const readBox = async (
  {
    malloc,
    call,
    readFloats,
  }: Pick<PdfiumApi, "call" | "malloc" | "readFloats">,
  page: PointerPage,
  boxType: "FPDFPage_GetMediaBox" | "FPDFPage_GetTrimBox"
): Promise<SpatialRectangle<Pixel>> => {
  const { pointer, free } = malloc(16 as Bytes)
  await call(
    boxType,
    page,
    pointer,
    (pointer + 4) as Pointer,
    (pointer + 8) as Pointer,
    (pointer + 12) as Pointer
  )
  const [left, bottom, right, top] = readFloats(pointer, 4)
  free()
  return {
    x: left as Pixel,
    y: bottom as Pixel,
    width: (right - left) as Pixel,
    height: (top - bottom) as Pixel,
  }
}

const CreateCurrentPlottingContext = async (
  api: PdfiumApi,
  context: Required<PlottingContext>,
  drawing: Page
): Promise<CurrentPlottingContext> => {
  const { safeCall, call, malloc, readFloats } = api
  const to_px = (value: Distance): Pixel =>
    (context.unit_to_px * value) as Pixel

  const get_size = ({
    width,
    height,
  }: Rectangle<Distance>): Rectangle<Pixel> => ({
    width: to_px(width),
    height: to_px(height),
  })

  const { width, height } = get_size(drawing)
  const bleed = to_px(drawing.bleed ?? context.page_bleed)

  const get_xy = ({ x: px, y: py }: Point<Distance>): Point<Pixel> => ({
    x: (bleed + to_px(px)) as Pixel,
    y: (bleed - to_px(py) + height) as Pixel,
  })

  const get_measures = (
    r: SpatialRectangle<Distance>
  ): SpatialRectangle<Pixel> => {
    const { x, y } = get_xy(r)
    const { width, height } = get_size(r)
    return {
      x,
      y: (y - height) as Pixel,
      width,
      height,
    }
  }

  const get_color = (id: string): Color | undefined => context.color_map[id]

  const get_page = async (blob_id: string): Promise<PageBlob | undefined> => {
    const promise = context.file_map[blob_id]
    if (!promise) return undefined

    const blob = await promise
    const reader = await ReadDocument(api, {
      stream: [blob],
      size: blob.size as Bytes,
    })

    const pageCount = await call("FPDF_GetPageCount", reader.doc_pointer)
    if (pageCount !== 1) {
      await CloseDocument(api, reader)
      throw new Error("PDF must have exactly one page")
    }

    const srcPage = await safeCall("FPDF_LoadPage", reader.doc_pointer, 0)
    const trimBox = await readBox(
      { malloc, call, readFloats },
      srcPage,
      "FPDFPage_GetTrimBox"
    )

    return {
      doc: reader.doc_pointer,
      page: srcPage,
      scale: context.scale_map[blob_id],
      trimBox,
      close: () => CloseDocument(api, reader),
    }
  }

  // Create new document
  const doc = await safeCall("FPDF_CreateNewDocument")
  const pageWidth = width + bleed * 2
  const pageHeight = height + bleed * 2
  const page = await safeCall("FPDFPage_New", doc, 0, pageWidth, pageHeight)

  // Set page boxes
  await call(
    "FPDFPage_SetTrimBox",
    page,
    bleed,
    bleed,
    bleed + width,
    bleed + height
  )
  await call("FPDFPage_SetMediaBox", page, 0, 0, pageWidth, pageHeight)

  return {
    api,
    doc,
    page,
    get_xy,
    get_page,
    get_measures,
    get_color,
    stroke_width: to_px(context.stroke_width),
    scale_bleed: to_px(context.scale_bleed),
  }
}

const plot_rect = async (
  context: CurrentPlottingContext,
  { fill_color, stroke_color, position, rotation = 0 as Degree }: PlotRect
): Promise<void> => {
  const {
    api,
    api: { call, safeCall },
    page,
    get_color,
    get_measures,
    stroke_width,
  } = context
  const fillColorValue = get_color(fill_color ?? "")
  const strokeColorValue = get_color(stroke_color ?? "")

  const { x, y, width, height } = get_measures(position)

  // Calculate rotated bottom-left corner position (same as pdflib)
  const { x: coord_x, y: coord_y } = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    x,
    y,
    rotation
  )

  // Create path at origin, will transform to final position
  const rect = await safeCall("FPDFPageObj_CreateNewPath", 0, 0)
  await call("FPDFPath_LineTo", rect, width, 0)
  await call("FPDFPath_LineTo", rect, width, height)
  await call("FPDFPath_LineTo", rect, 0, height)
  await call("FPDFPath_Close", rect)

  // Transform: [cos, sin, -sin, cos, tx, ty]
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))
  await call("FPDFPageObj_Transform", rect, cs, sn, -sn, cs, coord_x, coord_y)

  // Set colors and draw mode
  let drawMode = 0
  if (fillColorValue) {
    await setFillColor(fillColorValue)(api, rect)
    drawMode |= 1 // FPDF_FILLMODE_ALTERNATE
  }
  if (strokeColorValue) {
    await setStrokeColor(strokeColorValue)(api, rect)
    await call("FPDFPageObj_SetStrokeWidth", rect, stroke_width)
  }

  await call("FPDFPath_SetDrawMode", rect, drawMode, strokeColorValue ? 1 : 0)
  await call("FPDFPage_InsertObject", page, rect)
}

const plot_text = async (
  context: CurrentPlottingContext,
  {
    stroke_color,
    fill_color,
    position,
    rotation = 0 as Degree,
    text,
    font_size = 12,
    text_color,
    horizontal_align,
    vertical_align,
  }: PlotText
): Promise<void> => {
  const {
    api,
    api: { call, safeCall, writeString, malloc, readFloats },
    doc,
    page,
    get_color,
    get_measures,
  } = context

  // Draw background rect if colors specified
  if (stroke_color || fill_color) {
    await plot_rect(context, {
      func: PlotFunction.PLOT_RECT,
      rotation: 0 as Degree,
      stroke_color,
      fill_color,
      position,
    })
  }

  const textColor = get_color(text_color)
  if (!textColor) return

  // Create text object
  const { pointer: fontNamePtr, free: freeFontName } = writeString("Helvetica")
  const textObj = await safeCall(
    "FPDFPageObj_NewTextObj",
    doc,
    fontNamePtr,
    font_size
  )
  freeFontName()

  // Set text content
  const { pointer: textPtr, free: freeText } = encodeUtf16Le(api, text)
  await safeCall("FPDFText_SetText", textObj, textPtr)
  freeText()

  // Set text color
  await setFillColor(textColor)(api, textObj)

  // Get text bounds to measure
  const { pointer: boundsPtr, free: freeBounds } = malloc(16 as Bytes)
  await call(
    "FPDFPageObj_GetBounds",
    textObj,
    boundsPtr,
    (boundsPtr + 4) as Pointer,
    (boundsPtr + 8) as Pointer,
    (boundsPtr + 12) as Pointer
  )
  const [left, bottom, right] = readFloats(boundsPtr, 4)
  freeBounds()

  const fw = right - left
  // Use font_size * ascender ratio for Helvetica (matches pdflib's heightAtSize with descender: false)
  // Helvetica ascender is ~0.718 of the em square
  const fh = font_size * 0.718

  const { x, y, width, height } = get_measures(position)
  const { x: w, y: h } = Rotate(0 as Pixel, 0 as Pixel, width, height, rotation)

  let ew = 0
  let eh = 0

  if (horizontal_align === HorizontalAlign.LEFT) {
    ew = -(abs(w) - fw) / 2
  } else if (horizontal_align === HorizontalAlign.RIGHT) {
    ew = (abs(w) - fw) / 2
  }
  if (vertical_align === VerticalAlign.TOP) {
    eh = (abs(h) - fh) / 2
  } else if (vertical_align === VerticalAlign.BOTTOM) {
    eh = -(abs(h) - fh) / 2
  }

  // Calculate final text position with alignment and rotation
  const { x: coord_x, y: coord_y } = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    (x + width / 2 - fw / 2 + ew) as Pixel,
    (y + height / 2 - fh / 2 + eh) as Pixel,
    rotation
  )

  // Build transform: first translate text origin to 0,0, then rotate, then translate to final pos
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))

  // Combined transform: R * T where T translates (-left, -bottom) and then to coord
  // M = [cos, sin, -sin, cos, tx, ty]
  const tx = coord_x - (cs * left - sn * bottom)
  const ty = coord_y - (sn * left + cs * bottom)

  await call("FPDFPageObj_Transform", textObj, cs, sn, -sn, cs, tx, ty)

  await call("FPDFPage_InsertObject", page, textObj)
}

const plot_path = async (
  context: CurrentPlottingContext,
  { stroke_color, fill_color, points, closed }: PlotPath
): Promise<void> => {
  const {
    api,
    api: { call, safeCall },
    page,
    get_color,
    get_xy,
    stroke_width,
  } = context
  const strokeColorValue = get_color(stroke_color ?? "")
  const fillColorValue = get_color(fill_color ?? "")

  if (points.length === 0) return
  if (!strokeColorValue && !fillColorValue) return

  const [first, ...rest] = points.map(get_xy)

  const path = await safeCall("FPDFPageObj_CreateNewPath", first.x, first.y)

  for (const { x, y } of rest) {
    await call("FPDFPath_LineTo", path, x, y)
  }

  if (closed) {
    await call("FPDFPath_Close", path)
  }

  // Set colors and draw mode (matching pdflib behavior)
  const hasFill = !!fillColorValue && closed
  const hasStroke = !!strokeColorValue

  let drawMode = 0
  if (hasFill) {
    await setFillColor(fillColorValue)(api, path)
    drawMode |= 1 // FPDF_FILLMODE_ALTERNATE
  }
  if (hasStroke) {
    await setStrokeColor(strokeColorValue)(api, path)
    await call("FPDFPageObj_SetStrokeWidth", path, stroke_width)
  }

  await call("FPDFPath_SetDrawMode", path, drawMode, hasStroke ? 1 : 0)
  await call("FPDFPage_InsertObject", page, path)
}

const plot_media = async (
  context: CurrentPlottingContext,
  {
    page: pageId,
    crop_position,
    media_position,
    rotation = 0 as Degree,
  }: PlotMedia
): Promise<void> => {
  const {
    api: { call, safeCall },
    doc,
    page,
    get_page,
    get_measures,
    scale_bleed,
  } = context

  const spec = await get_page(pageId)
  if (!spec) return
  const { trimBox: inner, scale, page: srcPage, doc: srcDoc, close } = spec

  // Draw white background (CMYK 0,0,0,0 = white)
  const { x: cx, y: cy, width: cw, height: ch } = get_measures(crop_position)
  const bgRect = await safeCall("FPDFPageObj_CreateNewPath", cx, cy)
  await call("FPDFPath_LineTo", bgRect, cx + cw, cy)
  await call("FPDFPath_LineTo", bgRect, cx + cw, cy + ch)
  await call("FPDFPath_LineTo", bgRect, cx, cy + ch)
  await call("FPDFPath_Close", bgRect)
  await call("PDFKING_PageObj_SetFillColorCMYK", bgRect, 0, 0, 0, 0, 255)
  await call("FPDFPath_SetDrawMode", bgRect, 1, 0)
  await call("FPDFPage_InsertObject", page, bgRect)

  const media = get_measures(media_position)

  const { x: scale_x, y: scale_y } = ComputeScale<Pixel>(
    { outer: media, inner, bleed: scale_bleed },
    scale || undefined
  )

  const { x: tx, y: ty, width: tw, height: th } = inner
  const { x: mx, y: my, width: mw, height: mh } = media

  const center_x = (tx + tw / 2) as Pixel
  const center_y = (ty + th / 2) as Pixel

  // Calculate clip bounds in source page coordinates (same as pdflib mask)
  const { x: left, y: bottom } = Rotate(
    center_x,
    center_y,
    (center_x - (mx + mw / 2 - cx) / scale_x) as Pixel,
    (center_y - (my + mh / 2 - cy) / scale_y) as Pixel,
    -rotation as Degree
  )
  const { x: right, y: top } = Rotate(
    center_x,
    center_y,
    (center_x + (mw / 2 + (cx + cw - (mx + mw))) / scale_x) as Pixel,
    (center_y + (mh / 2 + (cy + ch - (my + mh))) / scale_y) as Pixel,
    -rotation as Degree
  )

  // Apply clip path to source page before creating XObject
  const srcClipPath = await safeCall(
    "FPDF_CreateClipPath",
    left,
    bottom,
    right,
    top
  )
  await call("FPDFPage_InsertClipPath", srcPage, srcClipPath)
  await call("FPDF_DestroyClipPath", srcClipPath)
  await call("FPDF_ClosePage", srcPage)

  // Create XObject from clipped source page
  const xobject = await safeCall("FPDF_NewXObjectFromPage", doc, srcDoc, 0)

  // Create form object from XObject
  const formObj = await safeCall("FPDF_NewFormObjectFromXObject", xobject)

  // Match pdflib: translate to (cx, cy), then rotate with todegree(rotation)
  // todegree(rotation) = degrees(-rotation)
  // For rotation=90, pdflib produces [0, 1, -1, 0]; for rotation=270, [0, -1, 1, 0]
  // We need negative rotation to match
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))

  const a = scale_x * cs
  const b = scale_x * sn
  const c = -scale_y * sn
  const d = scale_y * cs

  // pdflib places XObject at (cx, cy) with BBox starting at clip corner
  // Our XObject BBox starts at (0,0), so we offset by (left, bottom) of clip region
  const e = cx - a * left - c * bottom
  const f = cy - b * left - d * bottom

  await call("FPDFPageObj_Transform", formObj, a, b, c, d, e, f)

  // Insert into page
  await call("FPDFPage_InsertObject", page, formObj)

  // Cleanup
  await call("FPDF_CloseXObject", xobject)
  await close()
}

const handlers = {
  [PlotFunction.PLOT_TEXT]: plot_text,
  [PlotFunction.PLOT_RECT]: plot_rect,
  [PlotFunction.PLOT_MEDIA]: plot_media,
  [PlotFunction.PLOT_PATH]: plot_path,
} as const

export const PlotPage = async (
  page: Page,
  partial: PlottingContext = {}
): Promise<Blob> =>
  IterableToArray(
    ProvisionPdfium(async function* (api) {
      const { safeCall, call } = api
      const context = await CreateCurrentPlottingContext(
        api,
        CreateContext(partial),
        page
      )

      for (const shape of page.shapes) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (handlers[shape.func] as any)(context, shape)
      }

      await safeCall("FPDFPage_GenerateContent", context.page)
      await call("FPDF_ClosePage", context.page)

      yield* SaveDocument(api, context.doc)
      await call("FPDF_CloseDocument", context.doc)
    })
  ).then(ToBlob)
